// Client-side product analytics primitive.
//
//   import { track } from '@/lib/track'
//   track('signup.clicked', { location: 'hero' })
//
// Routes through api.track.ingest → events table. Fire-and-forget. Typed
// against EventCatalog so typos fail at compile time. Also mirrors to
// window.posthog.capture when PostHog is loaded.

import { ConvexReactClient } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { EventCatalog, EventName } from "../../convex/eventCatalog";
import { getOrCreateVisitorId } from "@/lib/analytics";
import { getAttribution } from "@/lib/attribution";

let convexClient: ConvexReactClient | null = null;

// Wired once from main.tsx so we don't pull a React context for fire-and-forget calls.
export function bindTrackClient(client: ConvexReactClient) {
  convexClient = client;
}

const lastFired: Record<string, number> = {};
const DEDUPE_MS = 1500;

export function track<K extends EventName>(name: K, props: EventCatalog[K]): void {
  // Dedupe identical event names within a short window (handles StrictMode double-invoke).
  const now = Date.now();
  const key = `${name}:${JSON.stringify(props ?? {})}`;
  if (lastFired[key] && now - lastFired[key] < DEDUPE_MS) return;
  lastFired[key] = now;

  const visitorId = getOrCreateVisitorId();
  const attribution = getAttribution();
  const sessionId = readPosthogSessionId();
  const path = typeof window !== "undefined" ? window.location.pathname : undefined;

  // PostHog mirror (client side) — fire-and-forget, no-op if not loaded.
  try {
    const ph = (typeof window !== "undefined" ? (window as any).posthog : null);
    if (ph && typeof ph.capture === "function") {
      ph.capture(name, props as any);
    }
  } catch { /* ignore */ }

  if (!convexClient) {
    // Pre-init or during SSR — drop. main.tsx binds before first render.
    return;
  }
  convexClient
    .mutation(api.track.ingest, {
      name,
      props: props as any,
      visitorId,
      sessionId,
      channel: attribution?.channel,
      referrer: attribution?.referrer,
      path,
    })
    .catch(() => { /* fire-and-forget */ });
}

function readPosthogSessionId(): string | undefined {
  try {
    const ph = (typeof window !== "undefined" ? (window as any).posthog : null);
    if (ph && typeof ph.get_session_id === "function") {
      return ph.get_session_id() ?? undefined;
    }
  } catch { /* ignore */ }
  return undefined;
}
